import { type ReactNode } from 'react';
import type { GameState } from '@bangos/shared';
import { useT, useTranslateSpectrum } from '../i18n';
import { Scoreboard } from './Scoreboard';
import { GuessingSlider } from './GuessingSlider';
import { RevealScreen } from './RevealScreen';
import { GameOverScreen } from './GameOverScreen';
import '../styles/gameplay.css';

interface RevealData {
  targetPosition: number;
  guessPosition: number;
  distance: number;
  points: number;
  team: 'blue' | 'red';
  guesserId: string;
}

interface GuesserViewProps {
  gameState: GameState;
  playerId: string;
  hasGuessed: boolean;
  guessPosition: number;
  revealData: RevealData | null;
  onPlayAgain?: () => void;
  onGuessConfirm: (position: number) => void;
}

export function GuesserView({
  gameState,
  playerId,
  hasGuessed,
  guessPosition,
  revealData,
  onPlayAgain,
  onGuessConfirm,
}: GuesserViewProps): ReactNode {
  const t = useT();
  const tSpectrum = useTranslateSpectrum();
  const { phase, currentRound } = gameState;
  const translatedSpectrum = currentRound?.spectrum ? tSpectrum(currentRound.spectrum) : null;

  const isCurrentPlayerPsychic = currentRound?.psychicId === playerId;
  const submittedClue = (currentRound?.clues?.length ?? 0) > 0 ? currentRound!.clues[0] : null;

  // --- Main render ---
  return (
    <>
      {/* Scoreboard */}
      <Scoreboard blueScore={gameState.blueScore} redScore={gameState.redScore} />

      {/* Spectrum — no dial marker for guessers */}
      {currentRound?.spectrum && translatedSpectrum && (
        <div className="spectrum-card">
          <div className="spectrum-title">{t('guesser.spectrumTitle')}</div>
          <div className="spectrum-concepts">
            <span className="spectrum-concept--left">{translatedSpectrum.left}</span>
            <span className="spectrum-concept--right">{translatedSpectrum.right}</span>
          </div>
        </div>
      )}

      {/* Waiting for psychic's clue */}
      {phase === 'psychicTurn' && !submittedClue && (
        <div className="clue-section">
          <div className="clue-thinking">
            {t('guesser.psychicThinking')}
          </div>
        </div>
      )}

      {/* Submitted clue display for guessers */}
      {submittedClue && (phase === 'guessing' || phase === 'psychicTurn') && (
        <div className="clue-section">
          <div className="clue-display">
            {t('guesser.psychicsClue', { clue: submittedClue.text })}
          </div>
        </div>
      )}

      {/* Guessing phase */}
      {phase === 'guessing' && (
        <>
          {!isCurrentPlayerPsychic ? (
            <GuessingSlider
              spectrum={currentRound!.spectrum}
              disabled={false}
              onConfirm={onGuessConfirm}
              initialPosition={guessPosition}
              hasGuessed={hasGuessed}
            />
          ) : (
            <div className="clue-section clue-waiting">
              <div className="clue-waiting-text">
                {hasGuessed
                  ? t('guesser.guessSubmittedWaiting')
                  : t('guesser.activeTeamGuessing')
                }
              </div>
            </div>
          )}
        </>
      )}

      {/* Reveal phase */}
      <RevealScreen gameState={gameState} revealData={revealData} submittedClue={submittedClue} playerId={playerId} />

      {/* Game Over phase */}
      <GameOverScreen gameState={gameState} onPlayAgain={onPlayAgain} />
    </>
  );
}
